import { forwardRef, useRef, useImperativeHandle, useState } from "react";
import ImageReportTable from "../component/ImageReportTable";


// --- Interfaces (Kept the same for context) ---
interface TableData {
  ReportHeader?: {
    RptName: string;
    [key: `Header${number}`]: string;
  };
  RptLayOut?: {
    RptColAlignInfo: (
      | "left"
      | "center"
      | "right"
      | "justify"
      | "fill"
      | "centerContinuous"
      | "distributed"
    )[];
    RptColWidthInfo: number[];
  };
  RptBodyHead: string[];
  RptBody: {
    Data: { DT: string[] }[];
    IsSelected?: number;
  }[];
  Header: string[];
  Footer: string[];
}


interface StockReportTableProps {
  columnDefs: string[];
  reportHeader: TableData["ReportHeader"];
  leftHeaders: string[];
  rightHeaders: string[];
  headerData: string[];
  footerData: string[];
  Caption: string;
  RptColWidthInfo: number[];
  RptColAlignInfo: (
    | "left"
    | "center"
    | "right"
    | "justify"
    | "fill"
    | "centerContinuous"
    | "distributed"
  )[];
  CPrintName: string;
  CAddress1: string;
  CAddress2: string;
  CAddress3: string;
  CAddress4: string;
  height: string;
  data?: TableData;
}


interface GridTableRef {
  exportToExcel: () => void;
  exportToPDF: () => void;
  getSelectedData: () => any[];
}


// --- Component with Select All Functionality ---
const StockReportTable = forwardRef<GridTableRef, StockReportTableProps>(
  (
    {
      columnDefs,
      reportHeader,
      leftHeaders,
      rightHeaders,
      headerData,
      footerData,
      Caption,
      RptColWidthInfo,
      RptColAlignInfo,
      CPrintName,
      CAddress1,
      CAddress2,
      CAddress3,
      CAddress4,
      height,
      data,
    },
    ref
  ) => {
    const gridRef = useRef<any>(null);
    const [zoomData, setZoomData] = useState<{
      image: string;
      row: any;
    } | null>(null);


    const [reportData, setReportData] = useState<TableData["RptBody"]>(
      data?.RptBody || []
    );


    useImperativeHandle(ref, () => ({
      exportToExcel: () => {
        console.log("StockReportTable: Triggering exportToExcel");
        gridRef.current?.exportToExcel();
      },
      exportToPDF: () => {
        console.log("StockReportTable: Triggering exportToPDF");
        gridRef.current?.exportToPDF();
      },
      getSelectedData: () => {
        const selectedOriginalIndices = reportData
          .map((row, index) => (row.IsSelected === 1 ? index : -1))
          .filter((index) => index !== -1);
          
        return selectedOriginalIndices.map(index => {
            const originalRow = (data?.RptBody || [])[index];
            return originalRow; 
        });
      },
    }));


    const normalizeHeader = (header: string) => header.replace(/[^\w]/g, "_");


    const validAlignments = [
      "left", "center", "right", "justify", "fill", "centerContinuous", "distributed",
    ] as const;
    const validateAlignment = (
      align: string
    ): (typeof validAlignments)[number] =>
      validAlignments.includes(align as any) ? (align as any) : "left";


    const formatCellValue = (value: any): string | string[] => {
      if (value === null || value === undefined) return "-";
      if (Array.isArray(value)) {
        return value
          .map((item) => {
            if (item === null || item === undefined) return "-";
            if (typeof item !== "string" || item.trim() === ":") return "";
            if (item.includes(":")) {
              const [label, val] = item.split(":", 2);
              return `${label.trim()}: ${val ? val.trim() : ""}`;
            }
            return item;
          })
          .filter((item) => item !== null && item !== undefined);
      }
      if (typeof value === "object") {
        return JSON.stringify(value, null, 2);
      }
      return String(value);
    };


    // 🌟 Select All Handler 🌟
    const handleSelectAll = () => {
      const allSelected = reportData.every(row => row.IsSelected === 1);
      
      setReportData((prevData) => {
        return prevData.map((row) => ({
          ...row,
          IsSelected: allSelected ? 0 : 1, // Toggle: if all selected, deselect all; otherwise select all
        }));
      });
    };


    // 🌟 Individual Checkbox Change Handler 🌟
    const handleCheckboxChange = (
      rowIndex: number,
      side: "left" | "right",
      data?: any
    ) => {
      const originalIndex =
        side === "left" ? data?.originalLeftIndex : data?.originalRightIndex;


      if (originalIndex === undefined || originalIndex >= reportData.length) return;


      console.log("✅ Checkbox clicked:", { side, rowIndex, originalIndex });


      setReportData((prevData) => {
        const newData = [...prevData];
        if (newData[originalIndex]) {
          const currentStatus = newData[originalIndex].IsSelected;
          newData[originalIndex] = {
            ...newData[originalIndex],
            IsSelected: currentStatus === 1 ? 0 : 1,
          };
        }
        return newData;
      });
    };


    // 🌟 Check if all items are selected 🌟
    const areAllSelected = reportData.length > 0 && reportData.every(row => row.IsSelected === 1);
    const areSomeSelected = reportData.some(row => row.IsSelected === 1);


    // 🌟 Custom Header Component with Select All Checkbox 🌟
    const CustomImageHeader = ({ side:_ }: { side: "left" | "right" }) => {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "8px",
            height: "100%",
          }}
        >
          <div
            style={{
              cursor: "pointer",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderRadius: "4px",
              lineHeight: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectAll();
            }}
          >
            <div
              style={{
                width: "18px",
                height: "18px",
                border: areAllSelected ? "2px solid #007bff" : areSomeSelected ? "2px solid #6c757d" : "2px solid #888",
                backgroundColor: areAllSelected ? "#007bff" : areSomeSelected ? "#6c757d" : "#fff",
                borderRadius: "3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.1s, border-color 0.1s",
              }}
            >
              {areAllSelected && (
                <span
                  style={{
                    color: "#fff",
                    fontSize: "1rem",
                    lineHeight: 1,
                    fontWeight: "bold",
                  }}
                >
                  ✓
                </span>
              )}
              {!areAllSelected && areSomeSelected && (
                <span
                  style={{
                    color: "#fff",
                    fontSize: "1rem",
                    lineHeight: 1,
                    fontWeight: "bold",
                  }}
                >
                  −
                </span>
              )}
            </div>
          </div>
          <span style={{ fontWeight: 600, fontSize: "0.9rem" }}>Image</span>
        </div>
      );
    };


    // 🌟 Image Column with Custom Header 🌟
    const createImageColumn = (side: "left" | "right") => ({
      headerName: "Image",
      field: `image_${side}`,
      width: 220,
      autoHeight: true,
      headerComponent: () => <CustomImageHeader side={side} />,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "8px",
      },
      cellRenderer: (params: any) => {
        const item = params.data[side];
        const imgUrl = item?.image || "https://placehold.co/200x200?text=No+Image";
        const itemExists = item.id !== undefined;


        if (side === 'right' && !itemExists) {
          return null;
        }


        const isSelected = itemExists && item.IsSelected === 1;
        const rowIndex = params.rowIndex;
        
        const floatingCheckbox = itemExists ? (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              zIndex: 10,
              cursor: 'pointer',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              borderRadius: '4px',
              lineHeight: 0,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleCheckboxChange(rowIndex, side, params.data);
            }}
          >
            <div 
              style={{
                width: '18px',
                height: '18px',
                border: isSelected ? '2px solid #007bff' : '2px solid #888',
                backgroundColor: isSelected ? '#007bff' : '#fff',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.1s, border-color 0.1s',
              }}
            >
              {isSelected && (
                <span 
                  style={{ 
                    color: '#fff', 
                    fontSize: '1rem', 
                    lineHeight: 1,
                    fontWeight: 'bold',
                  }}
                >
                  ✓
                </span>
              )}
            </div>
          </div>
        ) : null;



        return (
          <div
            style={{
              width: "200px",
              height: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              borderRadius: "8px",
              background: "#f8f9fa",
              position: 'relative',
              boxShadow: isSelected ? '0 0 0 3px #007bff' : 'none',
            }}
          >
            {floatingCheckbox}


            <img
              src={imgUrl}
              alt="product"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                cursor: "zoom-in",
                borderRadius: "6px",
              }}
              onClick={() =>
                setZoomData({
                  image: imgUrl,
                  row: params.data[side],
                })
              }
              onError={(e) => {
                e.currentTarget.src =
                  "https://placehold.co/200x200?text=No+Image";
              }}
            />
          </div>
        );
      },
    });


    const createDataColumns = (side: "left" | "right") =>
      columnDefs.map((header, index) => {
        const charCount = RptColWidthInfo[index] || 15;
        const calculatedWidth = Math.min(charCount * 10, 350);
        const alignment = validateAlignment(RptColAlignInfo[index] || "left");
        const cssTextAlign = (() => {
          switch (alignment) {
            case "left":
            case "center":
            case "right":
            case "justify":
              return alignment;
            case "fill":
            case "centerContinuous":
            case "distributed":
              return "left";
            default:
              return "left";
          }
        })();


        return {
          headerName: header,
          field: `${normalizeHeader(header)}_${side}`,
          width: calculatedWidth,
          autoHeight: true,
          flex: 1,
          cellStyle: {
            whiteSpace: "normal",
            lineHeight: "1.5",
            padding: "8px",
            textAlign: cssTextAlign,
            display: "flex",
            alignItems: "center",
            justifyContent: cssTextAlign === "center" ? "center" : cssTextAlign,
            minHeight: "80px",
          },
          cellRenderer: (params: any) => {
            if (side === 'right' && params.data.right.id === undefined) {
              return null;
            }


            const value = params.value;
            const formattedValue = formatCellValue(value);


            if (Array.isArray(formattedValue)) {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      alignment === "center" ? "center" : "flex-start",
                    justifyContent: "center",
                    gap: "4px",
                    height: "100%",
                    width: "100%",
                    fontSize: "20px",
                    fontStyle: "bold",
                    fontWeight: "600",
                    minHeight: "80px",
                  }}
                >
                  {formattedValue.map((item: string, idx: number) => {
                    if (item === null || item === undefined) return null;
                    const [label, val] = item.includes(":")
                      ? item.split(":", 2).map((s) => s.trim())
                      : ["", item];
                    return (
                      <div
                        key={idx}
                        style={{
                          fontSize: "1rem",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent:
                            alignment === "center" ? "center" : "flex-start",
                          textAlign: cssTextAlign,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#444",
                            marginRight: "4px",
                          }}
                        >
                          {label ? ` ${label} ${" "} :` : ""}
                        </span>
                        <span
                          style={{
                            color: "#222",
                            minHeight: "1.2em",
                          }}
                        >
                          {val !== undefined && val !== "" ? (
                            val
                          ) : (
                            <span
                              style={{
                                color: "#bbb",
                              }}
                            >
                              -
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            }


            return (
              <div style={{ textAlign: cssTextAlign, fontWeight: "bold" }}>
                {formattedValue === "" ? (
                  <span style={{ color: "#bbb" }}>-</span>
                ) : (
                  formattedValue
                )}
              </div>
            );
          },
        };
      });


    const colDefs = [
      ...[createImageColumn("left"), ...createDataColumns("left")],
      ...[createImageColumn("right"), ...createDataColumns("right")],
    ];


    // ✅ Data Transformation
    const originalRowData = (reportData || []).map((row, index) => {
      const rowObj: { [key: string]: any } = {
        id: index,
        image: row.Data?.[0]?.DT?.[0] || "", 
        IsSelected: row.IsSelected || 0,
        originalIndex: index,
      };
      
      columnDefs.forEach((header, i) => {
        const fieldData = row.Data?.[i + 1]?.DT || [];
        rowObj[normalizeHeader(header)] =
          fieldData.length > 1 ? fieldData : fieldData[0] || "";
      });
      return rowObj;
    });


    // ✅ Pair data and store original indices
    const pairedRowData = [];
    for (let i = 0; i < originalRowData.length; i += 2) {
      const left = originalRowData[i] || {};
      const right = originalRowData[i + 1] || {};


      const pairedRow: { [key: string]: any } = {
        left,
        right,
        image_left: left.image || "",
        image_right: right.image || "",
        originalLeftIndex: left.originalIndex,
        originalRightIndex: right.originalIndex,
      };


      columnDefs.forEach((header) => {
        const normHeader = normalizeHeader(header);
        pairedRow[`${normHeader}_left`] = left[normHeader] || "";
        pairedRow[`${normHeader}_right`] =
          right.id !== undefined ? right[normHeader] || "" : "";
      });


      pairedRowData.push(pairedRow);
    }


    return (
      <div>
        <ImageReportTable
          ref={gridRef}
          columnDefs={colDefs}
          rowData={pairedRowData}
          height={height}
          reportHeader={reportHeader || { RptName: "" }}
          enableSelection={false}
          leftHeaders={leftHeaders}
          rightHeaders={rightHeaders}
          headerData={headerData}
          footerData={footerData}
          Caption={Caption}
          RptColWidthInfo={RptColWidthInfo}
          RptColAlignInfo={RptColAlignInfo}
          CPrintName={CPrintName}
          CAddress1={CAddress1}
          CAddress2={CAddress2}
          CAddress3={CAddress3}
          CAddress4={CAddress4}
          domLayout="normal"
        />


        {/* Zoom Modal */}
        {zoomData && (
          <div
            onClick={() => setZoomData(null)}
            style={{
              position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center",
              justifyContent: "center", zIndex: 9999,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#fff", borderRadius: "8px", display: "flex", width: "80%",
                height: "80%", overflow: "hidden", boxShadow: "0 0 15px rgba(0,0,0,0.5)",
              }}
            >
              <div
                style={{
                  flex: "1", display: "flex", alignItems: "center", justifyContent: "center",
                  background: "#f9f9f9",
                }}
              >
                <img
                  src={zoomData.image}
                  alt="Zoomed"
                  style={{
                    maxWidth: "100%", maxHeight: "100%", minWidth: "200px",
                    borderRadius: "8px",
                  }}
                />
              </div>
              <div
                style={{
                  flex: "1", padding: "20px", overflowY: "auto", background: "#fff",
                }}
              >
                <h3
                  style={{
                    marginBottom: "20px", fontSize: "1.5rem", color: "#333", fontWeight: 600,
                  }}
                >
                  Details
                </h3>
                <table
                  style={{
                    width: "100%", borderCollapse: "collapse", fontSize: "0.9rem",
                    border: "1px solid #ccc",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "10px", background: "#f4f4f4", color: "#333", textAlign: "left",
                          fontWeight: 600, border: "1px solid #ccc",
                        }}
                      >
                        Field
                      </th>
                      <th
                        style={{
                          padding: "10px", background: "#f4f4f4", color: "#333", textAlign: "left",
                          fontWeight: 600, border: "1px solid #ccc",
                        }}
                      >
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(zoomData.row).map(([key, value], idx) => {
                      if (key === "id" || key === "image" || key === "IsSelected" || key === "originalIndex") return null;
                      return (
                        <tr
                          key={key}
                          style={{
                            background: idx % 2 === 0 ? "#fff" : "#fafafa",
                          }}
                        >
                          <td
                            style={{
                              padding: "12px", border: "1px solid #ccc", fontWeight: 500,
                              color: "#444", verticalAlign: "top",
                            }}
                          >
                            {columnDefs.find(
                              (header) => normalizeHeader(header) === key
                            ) || key}
                          </td>
                          <td
                            style={{
                              padding: "12px", border: "1px solid #ccc", color: "#222",
                              whiteSpace: "pre-wrap", wordBreak: "break-word", verticalAlign: "top",
                            }}
                          >
                            {formatCellValue(value)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);


export default StockReportTable;
